/**
 * SEE-226: a human label for the device a passkey is created on, so the passkey
 * management list can say "iPhone" instead of the backend's generic "Passkey"
 * default (see passkey.py's _default_name).
 *
 * The user agent is the only signal available — WebAuthn never reports the
 * authenticator's make or model — so this is best-effort by design. The user can
 * always correct it with renamePasskey.
 *
 * Labels are intentionally NOT translated: they are written once at registration
 * and stored server-side as data, so a Spanish-locale session would otherwise
 * permanently label the row in Spanish for every future session.
 */

/** Fallback when nothing matches — still reads correctly in the list. */
export const UNKNOWN_DEVICE_LABEL = 'This device';

/**
 * First match wins. iPhone/iPad are checked before the shell patterns because
 * the shell's UA (SpeedchainMobile/<os>/<version>) is appended to the WebView's
 * own UA, which carries the more specific model; the shell patterns are the
 * fallback for when it isn't.
 */
const DEVICE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/iPhone/i, 'iPhone'],
  [/iPad/i, 'iPad'],
  [/SpeedchainMobile\/ios/i, 'iOS device'],
  [/Android|SpeedchainMobile\/android/i, 'Android device'],
  // iPadOS Safari also reports "Macintosh"; an iPad mislabelled as a Mac is a
  // rename away, and there is no reliable way to tell them apart.
  [/Macintosh|Mac OS X/i, 'Mac'],
  [/Windows/i, 'Windows PC'],
  [/CrOS/i, 'Chromebook'],
  [/Linux/i, 'Linux device'],
];

/** Best-effort label for the current device, e.g. "iPhone" or "Windows PC". */
export function deviceLabel(userAgent?: string): string {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  const match = DEVICE_PATTERNS.find(([pattern]) => pattern.test(ua));
  return match ? match[1] : UNKNOWN_DEVICE_LABEL;
}

export default deviceLabel;
